/**
 * Cross-cutting API shapes. Mirrors what the Spring Boot backend actually returns
 * (see Phase 0 §4 and §5). Do not invent envelopes the backend doesn't produce.
 */

/**
 * Envelope emitted by the paginated web endpoints (farmer list, executive list,
 * volunteer list, reports preview). Server-side pagination is the only mode we support.
 */
export interface Paginated<T> {
  content: T[]
  totalElements: number
  page?: number
  size?: number
}

/**
 * The shape our web endpoints expect for POST bodies that filter+paginate.
 * Mirrors backend SearchCriteria. Every list endpoint takes a subset of these fields.
 */
export interface SearchCriteria {
  skip?: number
  limit?: number
  searchKeyWord?: string
  /**
   * Enrollment-date range on `u.created_date` (ISO `YYYY-MM-DD`). Inclusive
   * of both ends — backend treats `createdTo` as the whole day. Omit for no
   * date filter. Added alongside the Farmers-page Created-date filter.
   */
  createdFrom?: string
  createdTo?: string
  /**
   * State name (e.g. "Telangana", "Odisha") — exact match on `u.state`.
   * Backend `SearchCriteria.state` added 2026-09-06 alongside this field; the
   * console had a State picker for weeks that silently did nothing because
   * neither side carried the value. Omit / null = no state filter.
   */
  state?: string
  /**
   * District/mandal/village filters use the LOCATION NAME (matches
   * `u.district` / `u.mandal` / `u.village`), NOT the numeric id from
   * `/locations/*`. Callers must resolve id → name before sending. An
   * earlier `districtIds: number[]` shape existed here but the backend
   * `SearchCriteria.java` has no such field, so Jackson silently dropped
   * it — location filters looked wired up but never actually filtered
   * from 2026-08-18 through 2026-09-06.
   */
  district?: string
  mandal?: string
  village?: string
  /**
   * Filter by executive's display name. Backend does a LIKE match on
   * `CONCAT(first_name, ' ', last_name)` (see QueryBuilder line ~428).
   *
   * NOTE: the backend `SearchCriteria` has no `executiveId` field — a
   * previous version of this type declared one, but Jackson silently
   * dropped the property on the server, so filtering by exec was a
   * no-op. If exec-id filtering becomes needed, it requires a
   * matching backend field + a JOIN through user_association.
   */
  executiveName?: string
  /** Legacy single-project LIKE-substring filter. Kept for old callers; new
   *  code should prefer `projectNames` (exact IN match) — backend accepts
   *  both, `projectNames` wins when present. See sow_and_reap_backend#95. */
  projectName?: string
  projectNames?: string[]
  season?: string
  year?: string
  assigned?: boolean
  unAssigned?: boolean
  assignType?: 3 | 5 // 3=Executive, 5=Volunteer — see Phase 0 §4.1
  isFarmerWithoutVolunteer?: boolean
  orderBy?: Record<string, 'asc' | 'desc'>
  activeStatus?: FarmerActiveStatus
}

/**
 * Canonical `active_status` codes on any user record — farmer, executive,
 * volunteer, validator, and per-phase status rows. Reproduces Phase 0 §4.1
 * exactly.
 *
 * Cross-record contract: the backend uses ONE status enum across every
 * user role and phase row. `RECORD_STATUS.APPROVED` (5) means "verified"
 * for a farmer, "active" for staff or a validator, "approved" for a
 * phase. If the backend ever forks this per-record (e.g. staff = 4 while
 * farmer = 5), the naming split below is where the divergence surfaces.
 *
 * Naming rule:
 *   - `RECORD_STATUS` — use in code that reads a status without caring
 *     which record type it belongs to (shared table cells like
 *     `RowStatusCell`, generic status guards). This is the neutral name.
 *   - `FARMER_STATUS` — use in code that is unambiguously about a farmer
 *     record (farmer detail, farmer columns, phase status, plot status).
 *     Same object, different reader intent.
 *
 * Both are exported to make the intent explicit at the callsite. An
 * equality check against either is safe today because they point to the
 * same object; if that ever changes, the compiler forces the rename.
 */
export const RECORD_STATUS = {
  PENDING: 1,
  UNDER_REVIEW: 2,
  APPROVED: 5,
  REJECTED: 7,
} as const

/** Farmer-scoped alias of `RECORD_STATUS`. See the doc above for when to
 *  reach for this vs the neutral `RECORD_STATUS`. */
export const FARMER_STATUS = RECORD_STATUS

export type FarmerActiveStatus = (typeof RECORD_STATUS)[keyof typeof RECORD_STATUS]

/** Season enum — canonical spelling. Harvesting tab in Flutter uses the double-a
 *  variant `Vaanakalam`; we standardise here and translate on the wire if needed. */
export const SEASON = {
  VANAKALAM: 'VANAKALAM',
  YASANGI: 'YASANGI',
} as const
export type Season = (typeof SEASON)[keyof typeof SEASON]

/**
 * Role IDs — mirrors backend `com.agri.sowandreap.enums.Role`.
 *
 * Two "validator" identifiers exist for historical reasons:
 *   - `VOLUNTEER (5)` — the LEGACY validator role. Named VOLUNTEER on the
 *     backend but treated as "validator" by the old Flutter login gate
 *     (Phase 0 §9.1). Still in use for existing users.
 *   - `VALIDATOR (6)` — the CURRENT validator role introduced by backend
 *     PR #70 (validator management, FIELD/INTERNAL scoped access). New
 *     validators created via `POST /validator` are assigned this role.
 *
 * Both roles can log in to the console, subject to the login gate in
 * `src/features/auth/login-page.tsx` — VALIDATOR (6) requires
 * `validatorType === 'INTERNAL'` (FIELD validators use the separate
 * field-validator PWA). See issue #26.
 */
export const ROLE = {
  SUPER_ADMIN: 1,
  ADMIN: 2,
  EXECUTIVE: 3,
  FARMER: 4,
  VOLUNTEER: 5,
  VALIDATOR: 6,
  READ_ONLY_ADMIN: 8,
} as const
export type RoleId = (typeof ROLE)[keyof typeof ROLE]

/** Minimal person shape for pickers (executive / volunteer). */
export interface PersonRef {
  id: number
  firstName: string
  lastName?: string
  phone: string
}
